import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { tipsAPI, authAPI } from '@/utils/api';
import { getAuth, clearAuth } from '@/utils/auth';
import { useLang } from '@/utils/i18n';
import { toast } from 'sonner';
import { LogOut, TrendingUp, Trophy, Target, Zap, Settings, User, BarChart3, Rss, Swords, Headphones, Share2, Gamepad2, Brain, MessageCircle, Globe, Search } from 'lucide-react';
import TipCard from '@/components/TipCard';
import StatsCard from '@/components/StatsCard';
import NotificationSettings from '@/components/NotificationSettings';
import TipOfDay from '@/components/TipOfDay';
import LangSwitcher from '@/components/LangSwitcher';
import StreakBadge from '@/components/StreakBadge';
import NotificationBell from '@/components/NotificationBell';
import AICouncilPanel from '@/components/AICouncilPanel';
import LiveOddsIndicator from '@/components/LiveOddsIndicator';
import TipsterOfTheDay from '@/components/TipsterOfTheDay';
import AIRecommendations from '@/components/AIRecommendations';
import { MobileBottomNav, MobileMenu, MobileMenuButton } from '@/components/MobileNav';

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [user, setUser] = useState(null);
  const [tips, setTips] = useState([]);
  const [myTips, setMyTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');
  const [showSettings, setShowSettings] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const { token } = getAuth();
    if (!token) {
      navigate('/auth');
      return;
    }
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [userRes, tipsRes, myTipsRes] = await Promise.all([
        authAPI.getMe(),
        tipsAPI.getTips({ limit: 20 }),
        tipsAPI.getMyTips(),
      ]);
      setUser(userRes.data);
      setTips(Array.isArray(tipsRes.data) ? tipsRes.data : []);
      setMyTips(Array.isArray(myTipsRes.data) ? myTipsRes.data : []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error(t('error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuth();
    toast.success(t('logout'));
    navigate('/');
  };

  const handlePurchaseTip = async (tipId) => {
    try {
      await tipsAPI.purchaseTip(tipId);
      toast.success(t('buy_tip') + ' ✓');
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Kauf');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse-glow text-[#00BFA6] text-xl">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] pb-16 md:pb-0">
      {/* Header */}
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 md:px-8 py-3 md:py-4 flex justify-between items-center">
          <h1 className="text-lg md:text-2xl font-bold text-[#00BFA6]" data-testid="dashboard-title">
            {t('dashboard_title')}
          </h1>

          {/* Mobile: compact header */}
          <div className="flex items-center gap-1 md:hidden">
            <div className="text-right mr-1">
              <div className="font-mono text-lg font-bold text-[#00BFA6]" data-testid="tips-remaining-mobile">
                {user?.tips_remaining || 0}
              </div>
              <div className="text-[10px] text-gray-500">{t('remaining_tips')}</div>
            </div>
            <LangSwitcher />
            <NotificationBell />
            <MobileMenuButton onClick={() => setMenuOpen(true)} />
          </div>

          {/* Desktop: full header */}
          <div className="hidden md:flex items-center gap-1 md:gap-3">
            <LangSwitcher />
            <NotificationBell />
            <Button onClick={() => navigate('/profile')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="profile-link-btn">
              <User className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/analytics')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="analytics-link-btn">
              <BarChart3 className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/feed')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="feed-link-btn">
              <Rss className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/leaderboard')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="leaderboard-link-btn">
              <Trophy className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/challenges')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="challenges-link-btn">
              <Swords className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/support')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="support-link-btn">
              <Headphones className="w-5 h-5" />
            </Button>
            <Button onClick={() => setShowSettings(!showSettings)} variant="ghost" className="text-gray-400 hover:text-white" data-testid="settings-btn">
              <Settings className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/admin')} variant="ghost" className="text-[#7B1FA2] hover:text-white" data-testid="admin-link-btn">
              {t('admin')}
            </Button>
            <Button onClick={() => navigate('/simulator')} variant="ghost" className="text-[#00BFA6] hover:text-white" data-testid="simulator-link-btn">
              <Gamepad2 className="w-5 h-5" />
            </Button>
            <Button onClick={() => navigate('/ai-learning')} variant="ghost" className="text-[#7B1FA2] hover:text-white" data-testid="ai-learning-link-btn">
              <Brain className="w-5 h-5" />
            </Button>
            <div className="text-right">
              <div className="text-sm text-gray-400">{t('remaining_tips')}</div>
              <div className="font-mono text-2xl font-bold text-[#00BFA6]" data-testid="tips-remaining">{user?.tips_remaining || 0}</div>
            </div>
            <Button onClick={handleLogout} variant="ghost" className="text-gray-400 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      <div className="max-w-7xl mx-auto px-3 md:px-8 py-4 md:py-8">
        {/* Settings */}
        {showSettings && (
          <div className="mb-6">
            <NotificationSettings />
          </div>
        )}

        {/* Tip of Day */}
        <TipOfDay />

        {/* Streak Badge */}
        <StreakBadge />

        {/* AI Top Recommendations */}
        <AIRecommendations />

        {/* AI Council Panel */}
        <AICouncilPanel />

        {/* Tipster des Tages */}
        <TipsterOfTheDay />

        {/* Live Odds Indicator & Tip Sharing */}
        <div className="flex items-center justify-between mb-4">
          <LiveOddsIndicator sport="soccer" />
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/chat')}
              variant="ghost"
              className="text-[#00BFA6] hover:bg-[#00BFA6]/10 text-xs gap-1.5"
              data-testid="chat-link"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Live Chat
            </Button>
            <Button
              onClick={() => navigate('/ai-challenges')}
              variant="ghost"
              className="text-[#7B1FA2] hover:bg-[#7B1FA2]/10 text-xs gap-1.5"
              data-testid="ai-challenges-link"
            >
              <Swords className="w-3.5 h-3.5" /> User vs KI
            </Button>
            <Button
              onClick={() => navigate('/multi-sport')}
              variant="ghost"
              className="text-blue-400 hover:bg-blue-400/10 text-xs gap-1.5"
              data-testid="multi-sport-link"
            >
              <Globe className="w-3.5 h-3.5" /> Multi-Sport
            </Button>
            <Button
              onClick={() => navigate('/tip-sharing')}
              variant="ghost"
              className="text-[#00BFA6] hover:bg-[#00BFA6]/10 text-xs gap-1.5"
              data-testid="tip-sharing-link"
            >
              <Share2 className="w-3.5 h-3.5" /> Tip-Sharing
            </Button>
            <Button
              onClick={() => navigate('/daily-tournament')}
              variant="ghost"
              className="text-[#FFD700] hover:bg-[#FFD700]/10 text-xs gap-1.5"
              data-testid="daily-tournament-link"
            >
              <Trophy className="w-3.5 h-3.5" /> Daily Tournament
            </Button>
            <Button
              onClick={() => navigate('/weekly-jackpot')}
              variant="ghost"
              className="text-[#FFD700] hover:bg-[#FFD700]/10 text-xs gap-1.5"
              data-testid="weekly-jackpot-link"
            >
              <Zap className="w-3.5 h-3.5" /> Jackpot
            </Button>
            <Button
              onClick={() => navigate('/match-search')}
              variant="ghost"
              className="text-[#00BFA6] hover:bg-[#00BFA6]/10 text-xs gap-1.5"
              data-testid="match-search-link"
            >
              <Search className="w-3.5 h-3.5" /> Suche
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div data-testid="stats-overview" className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <StatsCard icon={<Trophy className="w-5 h-5 md:w-6 md:h-6" />} title={t('hit_rate')} value={`${user?.win_rate?.toFixed(1) || 0}%`} color="#4CAF50" />
          <StatsCard icon={<Target className="w-5 h-5 md:w-6 md:h-6" />} title={t('tips_used')} value={user?.tips_used || 0} color="#00BFA6" />
          <StatsCard icon={<TrendingUp className="w-5 h-5 md:w-6 md:h-6" />} title={t('correct_tips')} value={user?.total_tips_correct || 0} color="#4CAF50" />
          <StatsCard icon={<Zap className="w-5 h-5 md:w-6 md:h-6" />} title={t('sub_tier')} value={user?.subscription_tier?.toUpperCase() || 'FREE'} color="#7B1FA2" />
        </div>

        {/* Upgrade Notice */}
        {user?.tips_remaining === 0 && (
          <div className="mb-6 md:mb-8 p-4 md:p-6 bg-gradient-to-r from-[#7B1FA2] to-[#4A148C] rounded-xl" data-testid="upgrade-notice">
            <h3 className="text-lg md:text-xl font-bold mb-2">{t('no_tips')}</h3>
            <p className="text-sm md:text-base text-gray-200 mb-4">{t('upgrade_msg')}</p>
            <Button onClick={() => navigate('/pricing')} className="bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold" data-testid="upgrade-btn">
              {t('upgrade_now')}
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-4 md:mb-6 flex gap-4 border-b border-white/10 overflow-x-auto">
          <button
            onClick={() => setActiveTab('available')}
            className={`pb-3 px-3 md:px-4 text-sm md:text-base font-medium transition-colors whitespace-nowrap ${
              activeTab === 'available' ? 'text-[#00BFA6] border-b-2 border-[#00BFA6]' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-available-tips"
          >
            {t('available_tips')}
          </button>
          <button
            onClick={() => setActiveTab('my-tips')}
            className={`pb-3 px-3 md:px-4 text-sm md:text-base font-medium transition-colors whitespace-nowrap ${
              activeTab === 'my-tips' ? 'text-[#00BFA6] border-b-2 border-[#00BFA6]' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-my-tips"
          >
            {t('my_tips')}
          </button>
        </div>

        {/* Tips Grid */}
        <div data-testid="tips-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {activeTab === 'available' && tips.length === 0 && (
            <div className="col-span-2 text-center py-12 text-gray-400">{t('no_available')}</div>
          )}
          {activeTab === 'available' && tips.map((tip) => (
            <TipCard key={tip.id} tip={tip} onPurchase={handlePurchaseTip} canPurchase={user?.tips_remaining > 0} />
          ))}
          {activeTab === 'my-tips' && myTips.length === 0 && (
            <div className="col-span-2 text-center py-12 text-gray-400">{t('no_purchased')}</div>
          )}
          {activeTab === 'my-tips' && myTips.map((tip) => (
            <TipCard key={tip.id} tip={tip} isPurchased={true} />
          ))}
        </div>
      </div>

      {/* Legal Footer */}
      <footer className="mt-8 md:mt-12 py-4 md:py-6 bg-[#1E1E1E] border-t border-white/10 mb-14 md:mb-0">
        <div className="max-w-7xl mx-auto px-3 md:px-8">
          <div className="text-center text-xs md:text-sm text-gray-400">
            <p>
              <strong>{t('legal_warning')}</strong> {t('help_at')}{' '}
              <a href="https://www.bzga.de" className="text-[#00BFA6] underline" target="_blank" rel="noopener noreferrer">www.bzga.de</a>
              {' '}{t('adults_only')}
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[10px] md:text-xs">
              <a href="/impressum" className="hover:text-[#00BFA6]">Impressum</a>
              <a href="/datenschutz" className="hover:text-[#00BFA6]">Datenschutz</a>
              <a href="/agb" className="hover:text-[#00BFA6]">AGB</a>
              <a href="/faq" className="hover:text-[#00BFA6]">FAQ</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Bottom Nav */}
      <MobileBottomNav />
    </div>
  );
};

export default Dashboard;
